import { observable, computed } from 'mobx'
import { Model, type ModelProps } from '@/infra/model'
import { unixEpoch } from '@/utils/date-util'
import { Id } from '@/infra/id'
import { once } from 'lodash-es'
import { formatMoney } from '@/utils/money-util'

export type CouponProps = ModelProps<Coupon>

/**
 * A discount code.
 */
export class Coupon extends Model<Coupon> {
  id = Id.none
  workspaceId = Id.none

  @observable accessor code = ''
  @observable accessor friendlyName = ''
  @observable accessor description = ''
  @observable accessor maxTotalRedemptions = 0
  @observable accessor dateExpires: Date | null = null
  @observable accessor duration: CouponDuration = 'Once'
  @observable accessor couponType: CouponType = { type: 'Regular' }
  @observable accessor dateCreated = unixEpoch()
  @observable accessor dateModified = unixEpoch()
  @observable accessor offType: OffType = { type: 'AmountOff', amountOff: 0 }
  @observable accessor maxOff: MaxOff | null = null

  /**
   * Default instance.
   */
  static get none() {
    return lazyNone()
  }

  /**
   * The discount, as shown to users.
   */
  @computed
  get discount() {
    return this.offType.type === 'AmountOff'
      ? `${formatMoney(this.offType.amountOff)}-OFF`
      : `${this.offType.percentOff}%-OFF`
  }
}

const lazyNone = once(() => new Coupon())

/**
 * Plain old coupon.
 */
export interface RegularCouponType {
  type: 'Regular'
}
/**
 * In case we are feeling extra generous, we can add a custom discount
 * to specific users.
 */
export interface CustomDiscountCouponType {
  type: 'CustomDiscount'
  createdByUserId: string
}
/**
 * Used by the platform API to set up referral coupons for users.
 */
export interface ReferralCouponType {
  type: 'ReferralCode'
  referrerUserId: string
}
/**
 * Coupon Type Enum.
 */
export type CouponType =
  RegularCouponType | ReferralCouponType | CustomDiscountCouponType

/**
 * How many times can a single user redeem this coupon?
 * Note that a single user can at any time only have
 * a single unredeemed instance of the coupon at any time,
 * regardless of this value.
 */
export type CouponDuration = 'Once' | 'Forever'

/**
 * A Coupon that provides a fixed discount.
 */
export interface AmountOffType {
  type: 'AmountOff'
  amountOff: number
}
/**
 * A Coupon that provides a percent-based discount.
 */
export interface PercentOffType {
  type: 'PercentOff'
  percentOff: number
}
/**
 * The maximum amount discount on PercentType coupon
 */
export interface MaxAmountOffType {
  type: 'MaxAmountOff'
  amountOff: number
}
/**
 * The maximum percent off discount on AmountType coupon
 */
export interface MaxPercentOffType {
  type: 'MaxPercentOff'
  percentOff: number
}
/**
 * Off Type union.
 */
export declare type OffType = AmountOffType | PercentOffType
export declare type MaxOff = MaxAmountOffType | MaxPercentOffType
